import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import {
  DashboardLayout,
  MainContent,
  Sidebar,
} from 'components/DashboardLayout';
import DataTable, { CellPrimary } from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import ExportModal from 'components/ExportModal';
import YearSelect from 'components/FormFields/YearSelect';
import PageContainer from 'components/PageContainer';
import { HeaderContainer, HeaderTitle } from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';
import PageToolbar from 'components/PageToolbar';
import QuickActions from 'components/QuickActions';
import SearchInput from 'components/SearchInput';
import Toaster from 'components/Toaster';
// Year filter managed via URL params, no need for context
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { AppContext } from 'shared/AppContext';
import SelectUserModal from './components/SelectUserModal';
import { useGetTrackOpenCredits } from './gql/get-track-open-credits';
import { useGetTrackTotalOpenCredits } from './gql/get-track-total-open-credits';
import useOpenCreditsExport from './hooks/useOpenCreditsExport';

const TotalCreditsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
  margin-bottom: 16px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.08)'};
`;

const TotalLabel = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
`;

const TotalAmount = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: ${(props) => props.theme?.colors?.success || '#22C55E'};
`;

const TrackOpenCredit = () => {
  const [users, setUsers] = useState();
  const [count, setCount] = useState();
  const [showExportModal, setShowExportModal] = useState(false);
  const [showSelectUserModal, setShowSelectUserModal] = useState(false);
  const [toasterData, setToasterData] = useState(null);

  const { pathname } = useLocation();
  const userType = pathname.split('/').includes('admin-employee')
    ? 'employee'
    : 'track';
  const routePrefix = `/admin-${userType}`;

  const appContext = useContext(AppContext);
  const accountId = appContext?.state?.accountId;

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const updateQuery = useUpdateQuery();

  // Year filter - use URL param directly (no local state needed)
  const thisYear = Number(moment().format('YYYY'));
  const yearFilter = input.year ? Number(input.year) : thisYear;

  const handleYearChange = (e) => {
    const newYear = Number(e.target.value);
    updateQuery({ year: newYear, page: 1 });
  };

  const result = useGetTrackTotalOpenCredits();
  const { res, loading: creditsLoading } = useGetTrackOpenCredits();

  // Export hook
  const { startExport, subscribeToProgress, getDownloadUrl, isExporting } =
    useOpenCreditsExport();

  useEffect(() => {
    if (res && res.getTrackOpenCredits) {
      setUsers(res?.getTrackOpenCredits?.results);
      setCount(res?.getTrackOpenCredits?.count);
    }
  }, [res]);

  const loading = creditsLoading || !result.data;
  const totalCredit = result.data?.getTrackTotalOpenCredits || '$0.00';
  const totalPages = Math.ceil((count || 0) / 15);

  // Handle search - only reset page when user actively searches, not on mount
  const handleSearch = useCallback(
    (searchTerm) => {
      const currentQueryString = input.queryString || '';
      if (searchTerm !== currentQueryString) {
        updateQuery({ queryString: searchTerm, page: 1 });
      }
    },
    [updateQuery, input.queryString]
  );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  // Handle export with selected format
  const handleExport = useCallback(
    async (format) => {
      // Close modal
      setShowExportModal(false);

      // Show processing toaster
      setToasterData({
        title: `Exporting to ${format}...`,
        message: 'Preparing your export file',
        type: 'info',
        persistent: true,
      });

      try {
        // Build filters object
        const filters = {
          ...(input.queryString && { search: input.queryString }),
          ...(yearFilter && { year: yearFilter }),
        };

        // Start export mutation with format
        const jobId = await startExport(accountId, filters, format);

        // Subscribe to progress updates
        subscribeToProgress(jobId, {
          onProgress: (status) => {
            // Update toaster with progress
            if (status.progress && status.recordCount) {
              setToasterData({
                title: `Exporting to ${format}...`,
                message: `Processing ${status.recordCount} credits (${status.progress}%)`,
                type: 'info',
                persistent: true,
              });
            }
          },
          onComplete: async (data) => {
            try {
              // Get download URL
              const downloadUrl = await getDownloadUrl(jobId);

              // Show success toaster with download link
              setToasterData({
                title: 'Export Ready!',
                message: `${
                  data.recordCount || 0
                } credits exported to ${format}`,
                type: 'success',
                persistent: true,
                action: {
                  text: `Download ${format}`,
                  icon: 'download',
                  onClick: () => {
                    window.open(downloadUrl, '_blank');
                  },
                },
              });
            } catch (error) {
              setToasterData({
                title: 'Download Error',
                message: 'Failed to generate download link. Please try again.',
                type: 'error',
              });
            }
          },
          onError: (error) => {
            // Show error toaster
            let errorMessage =
              'An error occurred while exporting. Please try again.';

            if (error.message?.includes('permission')) {
              errorMessage =
                'You do not have permission to export from this account';
            } else if (error.message?.includes('not found')) {
              errorMessage = 'Export not found or has expired';
            }

            setToasterData({
              title: 'Export Failed',
              message: errorMessage,
              type: 'error',
            });
          },
        });
      } catch (error) {
        // Show error toaster for mutation failure
        setToasterData({
          title: 'Export Failed',
          message: 'Unable to start export. Please try again.',
          type: 'error',
        });
      }
    },
    [
      accountId,
      input.queryString,
      yearFilter,
      startExport,
      subscribeToProgress,
      getDownloadUrl,
    ]
  );

  // DataTable columns configuration
  const columns = [
    {
      field: 'formatted_name',
      header: 'Name',
      render: (user) => <CellPrimary>{user.formatted_name}</CellPrimary>,
    },
  ];

  // DataTable actions configuration
  const actions = [
    {
      type: 'view',
      title: 'View Details',
      to: (user) =>
        `${routePrefix}/openCredits/creditDetail/${user.id}?year=${yearFilter}`,
    },
  ];

  if (!result.data?.getTrackTotalOpenCredits) {
    return <div />;
  }

  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon icon="credits" />
            <HeaderTitle>Open Credits</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            {/* Content Card */}
            <Card noPadding>
              <ContentWrapper>
                {/* Total Credits Section */}
                <TotalCreditsSection>
                  <TotalLabel>Total Open Credits:</TotalLabel>
                  <TotalAmount>{totalCredit}</TotalAmount>
                </TotalCreditsSection>

                {/* Page Header with Search */}
                <PageToolbar>
                  <SearchInput
                    onSearch={handleSearch}
                    placeholder="Search Credits"
                    debounceMs={500}
                  />
                  <YearSelect
                    value={yearFilter}
                    onChange={handleYearChange}
                  />
                </PageToolbar>

                {/* DataTable */}
                <DataTable
                  columns={columns}
                  data={users}
                  loading={loading}
                  emptyMessage="No open credits found"
                  emptySubtext="Try adjusting your search or filters"
                  actions={actions}
                  keyField="id"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <QuickActions
              title="Quick Actions"
              actions={[
                {
                  label: 'Issue Credit',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5H0V4zm0 6v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2H0zm3 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm4 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                    </svg>
                  ),
                  onClick: () => setShowSelectUserModal(true),
                },
                {
                  label: 'Export Report',
                  icon: (
                    <svg
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                      <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
                    </svg>
                  ),
                  onClick: () => setShowExportModal(true),
                },
              ]}
            />
          </Sidebar>
        </DashboardLayout>

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          isExporting={isExporting}
          recordCount={count || 0}
          dataType="open credits"
        />

        {/* Select User Modal */}
        <SelectUserModal
          isOpen={showSelectUserModal}
          onClose={() => setShowSelectUserModal(false)}
        />

        {/* Toaster */}
        {toasterData && (
          <Toaster
            isOpen={!!toasterData}
            onClose={() => setToasterData(null)}
            title={toasterData.title}
            message={toasterData.message}
            type={toasterData.type}
            duration={toasterData.persistent ? 25000 : 10000}
            persistent={toasterData.persistent}
            action={toasterData.action}
          />
        )}
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackOpenCredit;
